import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../../utils/api";
import { LoginHelper } from "../../../utils/LoginHelper";
import HomeTagCarousel from "../../home/tag/HomeTagCarousel";
import Skeleton from "../../common/loading/Skeleton";
import ClipCard from "../clip/ClipCard";

const MAX_SLIDES = 12;

function clipMatchesExpertiseIds(clip, expertiseIds) {
	if (!expertiseIds.length) {
		return false;
	}
	const idSet = new Set(expertiseIds.map(String));
	if (Array.isArray(clip?.expertise_ids)) {
		if (clip.expertise_ids.some((id) => idSet.has(String(id)))) {
			return true;
		}
	}
	if (Array.isArray(clip?.expertises)) {
		return clip.expertises.some((item) => idSet.has(String(item?.id)));
	}
	return false;
}

function pickRelatedClips(allClips, clipId, expertiseIdList) {
	const others = (allClips ?? []).filter(
		(item) => String(item.id) !== String(clipId)
	);
	if (!others.length) {
		return [];
	}
	if (expertiseIdList.length) {
		const sameExpertise = others.filter((item) =>
			clipMatchesExpertiseIds(item, expertiseIdList)
		);
		if (sameExpertise.length) {
			return sameExpertise.slice(0, MAX_SLIDES);
		}
	}
	return others.slice(0, MAX_SLIDES);
}

export default function RelatedClipsCarousel({
	clipId,
	expertises,
	expertiseIds,
}) {
	const [related, setRelated] = useState([]);
	const [isLoading, setIsLoading] = useState(true);

	const expertiseIdList = useMemo(() => {
		const ids = new Set();
		(expertiseIds ?? []).forEach((id) => {
			if (id != null && id !== "") {
				ids.add(String(id));
			}
		});
		(expertises ?? []).forEach((item) => {
			if (item?.id != null) {
				ids.add(String(item.id));
			}
		});
		return [...ids];
	}, [expertises, expertiseIds]);

	const primaryExpertiseId = expertiseIdList[0] ?? "";

	const sectionTitle = useMemo(() => {
		const name = (expertises ?? []).map((e) => e?.name).find(Boolean);
		if (name) {
			return `کلیپ‌های پیشنهادی — ${name}`;
		}
		return "کلیپ‌های پیشنهادی";
	}, [expertises]);

	const listHref = primaryExpertiseId
		? `/video_clips/?expertise_id=${primaryExpertiseId}`
		: "/video_clips/";

	const expertiseKey = expertiseIdList.join("|");

	useEffect(() => {
		let cancelled = false;
		setIsLoading(true);

		const token = LoginHelper.getToken();

		const load = async () => {
			try {
				let list = [];
				if (primaryExpertiseId) {
					list = await api.videoClipList({
						token,
						expertise_id: primaryExpertiseId,
					});
				} else {
					list = await api.videoClipList({ token });
				}
				if (cancelled) {
					return;
				}
				let picked = pickRelatedClips(list, clipId, expertiseIdList);
				if (picked.length === 0) {
					const all = await api.videoClipList({ token });
					if (!cancelled) {
						picked = pickRelatedClips(all, clipId, expertiseIdList);
					}
				}
				if (!cancelled) {
					setRelated(picked);
				}
			} catch {
				if (!cancelled) {
					setRelated([]);
				}
			} finally {
				if (!cancelled) {
					setIsLoading(false);
				}
			}
		};

		load();

		return () => {
			cancelled = true;
		};
	}, [clipId, primaryExpertiseId, expertiseKey]);

	if (!isLoading && related.length === 0) {
		return null;
	}

	return (
		<section aria-label={sectionTitle} className="w-full">
			<div className="mb-4 flex items-center justify-between gap-3">
				<h2 className="font-Vazir-800 text-[18px] text-midGray">
					{sectionTitle}
				</h2>
				{!isLoading && (
					<Link
						to={listHref}
						className="shrink-0 font-Vazir-600 text-[13px] text-darkBlue hover:underline"
					>
						همه
					</Link>
				)}
			</div>

			{isLoading ? (
				<Skeleton variant="grid" type="videoclip" count={4} showTitle={false} />
			) : (
				<HomeTagCarousel
					swiperClassName="related-clips-swiper"
					slides={related.map((item) => ({
						key: item.id,
						node: <ClipCard clip={item} />,
					}))}
				/>
			)}
		</section>
	);
}
